import { useQuery } from "@tanstack/react-query";
import { DateTime } from "luxon";
import type { FC } from "react";
import React, { useState } from "react";

import type { LogEntry } from "@apis/calorie-grid/models";

import { GRID_COLUMNS, GRID_ROWS } from "../../../bootstrap/grid.ts";
import { useConfig } from "../../../shared/contexts/config/useConfig.ts";
import { useQueries } from "../../../shared/hooks/useQueries.ts";

import { EmptyState } from "./EmptyState.tsx";
import { GridCells } from "./GridCells.tsx";
import { GridLegend } from "./GridLegend.tsx";
import { LogEntryDetailsSheet } from "./LogEntryDetailsSheet.tsx";
import { LogEntryShape } from "./LogEntryShape.tsx";

interface PlacementGridProps {
  readonly onScanBarcode: () => void;
}

export const PlacementGrid: FC<PlacementGridProps> = ({ onScanBarcode }) => {
  const [detailsOpen, setDetailsOpen] = useState(false);

  const [detailsTarget, setDetailsTarget] = useState<LogEntry | null>(null);

  const { squareKcal } = useConfig();

  const { getLogEntriesQueryOptions } = useQueries();
  const logEntriesQueryResult = useQuery(getLogEntriesQueryOptions());
  const logEntries = logEntriesQueryResult.data ?? [];

  const openDetails = (logEntry: LogEntry): void => {
    setDetailsTarget(logEntry);
    setDetailsOpen(true);
  };

  // oxlint-disable-next-line no-warning-comments
  // TODO(APT-245): read "now" from a shared reactive context, not new Date().
  const today = DateTime.now().startOf("day").toJSDate();

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <GridLegend squareKcal={squareKcal} />
      <div className="min-h-0 flex-1 px-3 pt-2 pb-4">
        <div className="relative h-full overflow-hidden rounded-xl border bg-card shadow-sm">
          <svg
            className="absolute right-2 bottom-2 left-2"
            viewBox={`0 0 ${GRID_COLUMNS} ${GRID_ROWS}`}
          >
            <GridCells />
            {logEntries
              .filter(entry => entry.status === "dropped")
              .map(logEntry => (
                <LogEntryShape
                  key={logEntry.id}
                  logEntry={logEntry}
                  onSelect={openDetails}
                  today={today}
                />
              ))}
          </svg>
          {logEntries.length === 0 && (
            <EmptyState onScanBarcode={onScanBarcode} />
          )}
        </div>
      </div>
      <LogEntryDetailsSheet
        logEntry={detailsTarget}
        onOpenChange={setDetailsOpen}
        open={detailsOpen}
      />
    </div>
  );
};
